import React, { useCallback } from "react";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox/Checkbox";
import { Dialog, DialogFooter } from "components/ui/Dialog";

import { useVaccineValidationsModal } from "./hooks/useVaccineValidationsModal";
import { buildMitigatedRules } from "./utils/buildMitigatedRules";
import { formatValidationType } from "./utils/formatValidationType";
import styles from "./VaccineValidationsModal.module.css";

const ACK_TEXT =
  "I confirm I have reviewed this warning and wish to continue.";

export function VaccineValidationsModal({
  open,
  onClose,
  validations,
  isSubmitting,
  onContinue,
}) {
  const { items, hardStop, isComplete, getItemState, toggleAck, setReason } =
    useVaccineValidationsModal({ open, validations });

  const handleContinue = useCallback(() => {
    if (!isComplete) return;
    onContinue?.(buildMitigatedRules(items));
  }, [isComplete, items, onContinue]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Validation warnings"
      width={720}
      zIndex={1400}
      modal
    >
      <div className={styles.body}>
        <div className={styles.list}>
          {items.map((v, index) => {
            const { key, isHard, needsReason, needsAck, response } =
              getItemState(v, index);

            return (
              <div
                key={key}
                data-testid={`validation-item-${v?.rule ?? key}`}
                className={`${styles.item} ${
                  isHard ? styles.itemHard : styles.itemWarning
                }`.trim()}
              >
                <div className={styles.itemHeader}>
                  <span
                    className={`${styles.itemTitle} ${
                      isHard ? styles.itemTitleHard : styles.itemTitleWarning
                    }`.trim()}
                  >
                    {formatValidationType(v?.type)}
                  </span>
                </div>
                <p className={styles.itemMessage}>{v?.message || "-"}</p>
                {isHard && (
                  <p className={styles.hardStopNote}>
                    This warning blocks saving this vaccine. Please update the
                    vaccine details in the form and try again.
                  </p>
                )}

                {needsReason && (
                  <div className={styles.reasonWrap}>
                    <label
                      className={styles.reasonLabel}
                      htmlFor={`validation-reason-${key}`}
                    >
                      Reason (required)
                    </label>
                    <textarea
                      id={`validation-reason-${key}`}
                      className={styles.reasonInput}
                      value={response.reason ?? ""}
                      onChange={(e) => setReason(key, e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Enter reason to continue"
                    />
                  </div>
                )}

                {needsAck && (
                  <label className={styles.ackRow}>
                    <Checkbox
                      checked={Boolean(response.acknowledged)}
                      onChange={() => toggleAck(key)}
                      disabled={isSubmitting}
                    />
                    <span className={styles.ackText}>
                      {ACK_TEXT}
                    </span>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          size="large"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Close
        </Button>
        <Button
          variant="primary"
          size="large"
          onClick={hardStop ? undefined : handleContinue}
          disabled={hardStop || !isComplete || isSubmitting}
          title={
            hardStop
              ? "Hard stop — update vaccine details to proceed"
              : !isComplete
                ? "Complete all required fields to continue"
                : undefined
          }
        >
          {isSubmitting ? "Submitting..." : "Continue"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
